import type { AppointmentWithDisplay } from '@ergo/shared';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { STATUS_LABELS, doneLabel, titleOf } from '../appointments';
import { petals } from '../celebrations';
import { OutcomeButtons } from '../components/OutcomeButtons';
import { useResource } from '../hooks';
import { addDays, formatDayMonth, formatDuration, formatLongDate, timeOf, today } from '../time';
import { useUnlogged } from '../unlogged';

const RETURN_TO = '/pointage';
const LOGGED_DAYS = 14;

export function Pointage() {
  const { items, loading, error, refresh } = useUnlogged();
  const days = groupByDay(items);

  const logged = useResource(
    () => api.appointments.list({ from: addDays(today(), -LOGGED_DAYS), to: today() }),
    [],
  );
  const loggedItems = (logged.data ?? [])
    .filter((item) => item.status !== 'planned')
    .sort((a, b) => b.startsAt.localeCompare(a.startsAt));

  // Les pétales tombent quand SON geste vide la liste — pas en ouvrant une page
  // déjà à jour : le drapeau ne se lève qu'au pointage, et retombe aussitôt.
  const acted = useRef(false);
  const onLogged = () => {
    acted.current = true;
    refresh();
    logged.reload();
  };

  useEffect(() => {
    if (acted.current && !loading && items.length === 0) {
      acted.current = false;
      petals();
    }
  }, [items.length, loading]);

  return (
    <section className="page">
      <header className="page__header">
        <div>
          <h1 className="page__title">Pointage</h1>
          <p className="page__lead">
            Les séances passées qui attendent encore une durée ou un motif d’absence.
          </p>
        </div>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {loading && items.length === 0 && <p className="muted">Chargement…</p>}

      {!loading && items.length === 0 && (
        <p className="empty empty--fete">Tout est pointé 🎉</p>
      )}

      {days.map(([date, appointments]) => (
        <section className="pointage__day" key={date}>
          <h2 className="pointage__date">{formatLongDate(date)}</h2>
          <ul className="cards">
            {appointments.map((appointment) => (
              <li key={appointment.id}>
                <article className="card">
                  <div className={`card__title${appointment.important ? ' is-important' : ''}`}>
                    <Link to={`/rdv/${appointment.id}`} state={{ from: RETURN_TO }}>
                      {titleOf(appointment)}
                    </Link>
                  </div>
                  <p className="card__meta">
                    {timeOf(appointment.startsAt)} · {formatDuration(appointment.plannedDurationMin)}{' '}
                    prévues · {appointment.locationLabel}
                  </p>
                  <OutcomeButtons appointment={appointment} onLogged={onLogged} />
                </article>
              </li>
            ))}
          </ul>
        </section>
      ))}

      {loggedItems.length > 0 && (
        <section className="pointage__day">
          <h2 className="pointage__date">Pointées récemment</h2>
          <ul className="cards">
            {loggedItems.map((appointment) => (
              <li key={appointment.id}>
                <article className="card card--muted">
                  <div className={`card__title${appointment.important ? ' is-important' : ''}`}>
                    <Link to={`/rdv/${appointment.id}`} state={{ from: RETURN_TO }}>
                      {titleOf(appointment)}
                    </Link>
                  </div>
                  <p className="card__meta">
                    {formatDayMonth(appointment.startsAt.slice(0, 10))} ·{' '}
                    {timeOf(appointment.startsAt)} ·{' '}
                    {appointment.status === 'done' && appointment.actualDurationMin !== null
                      ? doneLabel(appointment.actualDurationMin, appointment.free)
                      : STATUS_LABELS[appointment.status]}
                  </p>
                </article>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        className="fab"
        to={`/rdv/nouveau?date=${today()}&status=done`}
        state={{ from: RETURN_TO }}
        aria-label="Pointer une séance imprévue"
      >
        +
      </Link>
    </section>
  );
}

/** Oldest first, grouped by day — the order she works through them in the evening. */
function groupByDay(items: AppointmentWithDisplay[]): [string, AppointmentWithDisplay[]][] {
  const days = new Map<string, AppointmentWithDisplay[]>();

  for (const item of [...items].sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
    const day = item.startsAt.slice(0, 10);
    const bucket = days.get(day);
    if (bucket) bucket.push(item);
    else days.set(day, [item]);
  }

  return [...days.entries()];
}
